import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReportNote from '../components/ReportNote';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';
const TARGET_SECONDS = 39 * 3600;
const DAY_TARGET = 7.8 * 3600;

const MEMBER_START_DATES = {
  'BUIDIN': '2026-08-17',
};

function memberStart(assignee) {
  const last = (assignee || '').split(' ').pop().toUpperCase();
  return MEMBER_START_DATES[last] || '2026-01-01';
}

const BE_HOLIDAYS = new Set([
  '2026-01-01', // Nouvel An
  '2026-04-06', // Lundi de Pâques
  '2026-05-01', // Fête du Travail
  '2026-05-14', // Ascension
  '2026-05-25', // Lundi de Pentecôte
  '2026-07-21', // Fête Nationale
  '2026-11-11', // Armistice
  '2026-12-25', // Noël
]);

const PERSON_COLORS = {
  'BACKER':    '#f97316',
  'ATAUIL':    '#f59e0b',
  'BARBIEUX':  '#ec4899',
  'BASHORUN':  '#3b82f6',
  'DEMORTIER': '#10b981',
  'BUIDIN':    '#8b5cf6',
};

function personColor(assignee) {
  const last = (assignee || '').split(' ').pop().toUpperCase();
  return PERSON_COLORS[last] || '#94a3b8';
}

function localDateStr(d) {
  return [d.getFullYear(), String(d.getMonth()+1).padStart(2,'0'), String(d.getDate()).padStart(2,'0')].join('-');
}

function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return localDateStr(d);
}

function getMondayOf(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return localDateStr(d);
}

function getWeekDays(mondayStr) {
  return Array.from({length: 5}, (_, i) => addDays(mondayStr, i));
}

function fmtHPlain(seconds) {
  if (!seconds) return '—';
  const h = seconds / 3600;
  const hh = Math.floor(h);
  const m = Math.round((h % 1) * 60);
  return m > 0 ? `${hh}h${String(m).padStart(2,'0')}` : `${hh}h`;
}

function fmtSeconds(s) {
  if (!s) return '—';
  const h = s / 3600;
  const m = Math.round((h % 1) * 60);
  const hh = Math.floor(h);
  return m > 0 ? `${hh}h${String(m).padStart(2,'0')}` : `${hh}h`;
}

function weekLabel(mondayStr) {
  const fri = addDays(mondayStr, 4);
  const m = new Date(mondayStr + 'T00:00:00');
  const f = new Date(fri + 'T00:00:00');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  if (m.getMonth() === f.getMonth())
    return `${m.getDate()}–${f.getDate()} ${months[m.getMonth()]}`;
  return `${m.getDate()} ${months[m.getMonth()]} – ${f.getDate()} ${months[f.getMonth()]}`;
}

function dayLabel(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  return { day: days[d.getDay()], num: d.getDate() };
}

function monthLabel(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  return `${months[d.getMonth()]} ${d.getFullYear()}`;
}

function countWorkingDays(fromStr, toStr) {
  let count = 0;
  let cur = fromStr;
  while (cur <= toStr) {
    const dow = new Date(cur + 'T00:00:00').getDay();
    if (dow >= 1 && dow <= 5 && !BE_HOLIDAYS.has(cur)) count++;
    cur = addDays(cur, 1);
  }
  return count;
}

function getMonthWeeks(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const firstMonday = getMondayOf(localDateStr(firstDay));
  const weeks = [];
  let cur = firstMonday;
  while (cur <= localDateStr(lastDay)) {
    weeks.push(cur);
    cur = addDays(cur, 7);
  }
  return weeks;
}

function cellStyle(seconds, target) {
  if (!seconds) return { background: 'transparent', color: 'var(--text-3)', fontWeight: 400 };
  const pct = seconds / target;
  if (pct >= 1.0) return { background: '#16a34a22', color: '#16a34a', fontWeight: 700 };
  if (pct >= 0.75) return { background: '#d9770622', color: '#d97706', fontWeight: 700 };
  return { background: '#dc262622', color: '#dc2626', fontWeight: 700 };
}

function totalBadge(seconds, target = TARGET_SECONDS) {
  const pct = seconds / target;
  let bg, color;
  if (pct >= 1.0) { bg = '#16a34a22'; color = '#16a34a'; }
  else if (pct >= 0.77) { bg = '#d9770622'; color = '#d97706'; }
  else { bg = '#dc262622'; color = '#dc2626'; }
  return (
    <span style={{ display:'inline-flex', flexDirection:'column', alignItems:'center', padding:'4px 10px', borderRadius:10,
      background:bg, color, fontWeight:700, fontSize:13, gap:1 }}>
      {fmtHPlain(seconds)}
      <span style={{ fontSize:9, fontWeight:400, opacity:0.75 }}>/ {fmtHPlain(target)}</span>
    </span>
  );
}

function groupByPerson(rows) {
  const map = {};
  const absMap = {};
  for (const r of rows) {
    if (!map[r.assignee]) { map[r.assignee] = {}; absMap[r.assignee] = {}; }
    map[r.assignee][r.date] = (map[r.assignee][r.date] || 0) + r.seconds;
    absMap[r.assignee][r.date] = (absMap[r.assignee][r.date] || 0) + (r.abs_seconds || 0);
  }
  return { byPerson: map, absByPerson: absMap };
}

function shortName(assignee) {
  const parts = (assignee || '').split(' ');
  if (parts.length < 2) return assignee;
  return `${parts[0]} ${parts[parts.length-1]}`;
}

// ── Drill-down popover ─────────────────────────────────────────
function DetailPopover({ assignee, date, jiraUrl, onClose }) {
  const [rows, setRows] = useState(null);
  const ref = useRef();

  useEffect(() => {
    fetch(`${API}/api/reports/billable-time/detail?assignee=${encodeURIComponent(assignee)}&date=${date}`)
      .then(r => r.json()).then(setRows).catch(() => setRows([]));
  }, [assignee, date]);

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) onClose(); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const d = new Date(date + 'T00:00:00');
  const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const dateLabel = `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}`;
  const color = personColor(assignee);

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.25)', zIndex:600, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div ref={ref} style={{ background:'var(--bg)', borderRadius:14, boxShadow:'0 8px 40px rgba(0,0,0,.35)',
        width:560, maxHeight:'80vh', display:'flex', flexDirection:'column', overflow:'hidden', border:'1px solid var(--border)' }}>
        {/* Header */}
        <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:10, height:10, borderRadius:'50%', background:color, flexShrink:0 }} />
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:700, fontSize:15 }}>{assignee}</div>
            <div style={{ fontSize:12, color:'var(--text-2)', marginTop:2 }}>{dateLabel}</div>
          </div>
          {rows && (
            <div style={{ fontWeight:700, fontSize:14, color:'var(--text)' }}>
              {fmtHPlain(rows.reduce((s,r)=>s+r.seconds,0))} total
            </div>
          )}
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer',
            color:'var(--text-3)', fontSize:20, lineHeight:1, padding:'0 4px', marginLeft:8 }}>×</button>
        </div>
        {/* Entries */}
        <div style={{ overflowY:'auto', padding:'8px 0' }}>
          {rows === null && <div style={{ padding:24, textAlign:'center', color:'var(--text-2)' }}>Loading…</div>}
          {rows !== null && rows.length === 0 && <div style={{ padding:24, textAlign:'center', color:'var(--text-2)' }}>No entries</div>}
          {rows !== null && rows.map((r, i) => (
            <div key={i} style={{ padding:'10px 20px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'flex-start', gap:12 }}>
              <div style={{ minWidth:80, flexShrink:0 }}>
                {jiraUrl
                  ? <a href={`${jiraUrl}/browse/${r.issue_key}`} target="_blank" rel="noreferrer"
                      style={{ fontFamily:'monospace', fontWeight:700, fontSize:12, color:'var(--accent)', textDecoration:'none' }}>
                      {r.issue_key}
                    </a>
                  : <span style={{ fontFamily:'monospace', fontWeight:700, fontSize:12 }}>{r.issue_key}</span>
                }
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                  title={r.summary}>{r.summary || '—'}</div>
                {r.bc_project && <div style={{ fontSize:11, color:'var(--text-3)', marginTop:2 }}>{r.bc_project}</div>}
              </div>
              <div style={{ flexShrink:0, fontWeight:700, fontSize:13, color:'var(--accent)', minWidth:48, textAlign:'right' }}>
                {fmtSeconds(r.seconds)}
              </div>
              {r.started_time && (
                <div style={{ flexShrink:0, fontSize:11, color:'var(--text-3)', minWidth:36 }}>{r.started_time}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Clickable cell ─────────────────────────────────────────────
function holidayBadge(date) {
  if (!BE_HOLIDAYS.has(date)) return null;
  return (
    <span style={{ display:'inline-block', marginTop:3, padding:'1px 6px', borderRadius:8,
      background:'#f59e0b22', color:'#b45309', fontSize:10, fontWeight:700, letterSpacing:'0.02em' }}>
      Public Holiday
    </span>
  );
}

function absLabel(absSec) {
  if (!absSec) return null;
  const h = absSec / 3600;
  const label = h >= 7 ? 'Off' : 'Off ½';
  return (
    <span style={{ display:'inline-block', marginTop:3, padding:'1px 6px', borderRadius:8,
      background:'#6366f122', color:'#6366f1', fontSize:10, fontWeight:700, letterSpacing:'0.02em' }}>
      {label}
    </span>
  );
}

function Cell({ seconds, absSec, target, assignee, date, jiraUrl, disabled }) {
  const [open, setOpen] = useState(false);
  const cs = cellStyle(seconds, target);
  const hasData = seconds > 0;

  return (
    <>
      <div onClick={() => hasData && !disabled && setOpen(true)}
        style={{ padding:'6px 4px', borderRadius:8, fontSize:13, minWidth:52, transition:'opacity .1s',
          background:cs.background, color:cs.color, fontWeight:cs.fontWeight,
          cursor: hasData && !disabled ? 'pointer' : 'default',
          opacity: hasData && !disabled ? 1 : undefined }}>
        {hasData
          ? <span style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:0 }}>
              <span style={{ display:'flex', alignItems:'center', gap:4 }}>
                {fmtHPlain(seconds)}
                {!disabled && <span style={{ fontSize:9, opacity:.5 }}>▾</span>}
              </span>
              {absLabel(absSec)}
              {holidayBadge(date)}
            </span>
          : <span style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:0 }}>
              <span>—</span>
              {absLabel(absSec)}
              {holidayBadge(date)}
            </span>}
      </div>
      {open && <DetailPopover assignee={assignee} date={date} jiraUrl={jiraUrl} onClose={() => setOpen(false)} />}
    </>
  );
}

const TH = ({ children, style }) => (
  <th style={{ padding:'10px 8px', textAlign:'center', fontSize:10, color:'var(--text-3)',
    fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em', ...style }}>
    {children}
  </th>
);

export default function BillableReport() {
  const today = localDateStr(new Date());
  const MIN_DATE = '2026-01-01';

  const [view, setView] = useState('week');
  const [weekStart, setWeekStart] = useState(getMondayOf(today));
  const [monthYear, setMonthYear] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() });
  const [customFrom, setCustomFrom] = useState('2026-01-01');
  const [customTo, setCustomTo] = useState(today);
  const [compareWeekStart, setCompareWeekStart] = useState(getMondayOf(today));
  const [compareMember, setCompareMember] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [jiraUrl, setJiraUrl] = useState(localStorage.getItem('jiraUrl') || '');

  useEffect(() => {
    fetch(`${API}/api/settings`).then(r => r.json()).then(d => {
      if (d.jira_url) { setJiraUrl(d.jira_url); localStorage.setItem('jiraUrl', d.jira_url); }
    }).catch(() => {});
  }, []);

  const fromDate = view === 'week' ? weekStart
    : view === 'month' ? localDateStr(new Date(monthYear.year, monthYear.month, 1))
    : view === 'compare' ? compareWeekStart
    : customFrom;

  const toDate = view === 'week' ? addDays(weekStart, 4)
    : view === 'month' ? localDateStr(new Date(monthYear.year, monthYear.month + 1, 0))
    : view === 'compare' ? addDays(compareWeekStart, 4)
    : customTo;

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/reports/billable-time?from_date=${fromDate}&to_date=${toDate}`)
      .then(r => r.json())
      .then(d => { setData(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const { byPerson, absByPerson } = groupByPerson(data);
  const people = Object.keys(byPerson).sort();

  // ── WEEK VIEW ──────────────────────────────────────────────────
  const renderWeek = () => {
    const days = getWeekDays(weekStart);
    const canPrev = weekStart > MIN_DATE;
    const canNext = addDays(weekStart, 7) <= today;
    const isCurrentWeek = weekStart === getMondayOf(today);
    const yesterday = addDays(today, -1);
    const weekFriday = addDays(weekStart, 4);
    const cutoff = isCurrentWeek ? (yesterday < weekStart ? weekStart : yesterday > weekFriday ? weekFriday : yesterday) : weekFriday;
    const effectiveDays = countWorkingDays(weekStart, cutoff);
    const weekWorkingDays = days.filter(d => !BE_HOLIDAYS.has(d)).length;
    const effectiveTarget = effectiveDays * DAY_TARGET;

    return (
      <div>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
          <button onClick={() => canPrev && setWeekStart(w => addDays(w, -7))}
            className="btn btn-secondary" style={{padding:'6px 12px', opacity:canPrev?1:.4}}>‹</button>
          <span style={{ fontWeight:700, fontSize:15, minWidth:180, textAlign:'center' }}>{weekLabel(weekStart)}</span>
          <button onClick={() => canNext && setWeekStart(w => addDays(w, 7))}
            className="btn btn-secondary" style={{padding:'6px 12px', opacity:canNext?1:.4}}>›</button>
        </div>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', minWidth:580 }}>
            <thead>
              <tr style={{ borderBottom:'2px solid var(--border)' }}>
                <TH style={{ textAlign:'left', padding:'10px 14px', width:170 }}>Member</TH>
                {days.map(d => {
                  const { day, num } = dayLabel(d);
                  return (
                    <TH key={d}>
                      <div>{day}</div>
                      <div style={{ fontSize:14, color:'var(--text)', marginTop:2, fontWeight:700 }}>{num}</div>
                    </TH>
                  );
                })}
                <TH>Total</TH>
                <TH>
                  <div>vs target</div>
                  <div style={{ fontWeight:400, fontSize:10, color:'var(--text-3)', marginTop:2 }}>
                    {effectiveDays}d × 7.8h
                  </div>
                </TH>
              </tr>
            </thead>
            <tbody>
              {people.length === 0 && (
                <tr><td colSpan={9} style={{ padding:32, textAlign:'center', color:'var(--text-2)', fontSize:13 }}>No logged time for this week</td></tr>
              )}
              {people.map(person => {
                const map = byPerson[person] || {};
                const absMap = absByPerson[person] || {};
                const start = memberStart(person);
                const effectiveDays2 = (isCurrentWeek ? days.filter(d => d <= yesterday) : days).filter(d => d >= start);
                const weekTotal = effectiveDays2.reduce((s, d) => s + (map[d] || 0), 0);
                const personTarget = effectiveDays2.reduce((s, d) => {
                  if (BE_HOLIDAYS.has(d)) return s;
                  const absSec = absMap[d] || 0;
                  return s + Math.max(0, DAY_TARGET - absSec);
                }, 0);
                const diff = weekTotal - personTarget;
                return (
                  <tr key={person} style={{ borderBottom:'1px solid var(--border)' }}>
                    <td style={{ padding:'10px 14px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ width:8, height:8, borderRadius:'50%', background:personColor(person), flexShrink:0 }} />
                        <span style={{ fontWeight:600, fontSize:13 }}>{shortName(person)}</span>
                      </div>
                    </td>
                    {days.map(d => (
                      <td key={d} style={{ padding:'6px 4px', textAlign:'center' }}>
                        {d < start
                          ? <span style={{ color:'var(--text-3)', fontSize:13 }}>—</span>
                          : <Cell seconds={map[d]||0} absSec={absMap[d]||0} target={DAY_TARGET} assignee={person} date={d} jiraUrl={jiraUrl} />}
                      </td>
                    ))}
                    <td style={{ padding:'8px 14px', textAlign:'center' }}>{totalBadge(weekTotal, personTarget)}</td>
                    <td style={{ padding:'8px 14px', textAlign:'center', fontSize:13, fontWeight:700,
                      color: diff >= 0 ? '#16a34a' : '#dc2626' }}>
                      {personTarget === 0 ? '—' : (() => { const s = fmtHPlain(Math.abs(diff)); return diff >= 0 ? `+${s}` : `-${s}`; })()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {people.length > 0 && (
              <tfoot>
                <tr style={{ borderTop:'2px solid var(--border)', background:'var(--bg-2)' }}>
                  <td style={{ padding:'10px 14px', fontWeight:700, fontSize:12, color:'var(--text-2)' }}>Team</td>
                  {days.map(d => {
                    const tot = people.reduce((s, p) => s + (byPerson[p][d] || 0), 0);
                    const isFuture = isCurrentWeek && d >= today;
                    return <td key={d} style={{ padding:'8px 4px', textAlign:'center', fontWeight:700, fontSize:13, color: isFuture ? 'var(--text-3)' : 'var(--text)', opacity: isFuture ? 0.4 : 1 }}>{tot ? fmtHPlain(tot) : '—'}</td>;
                  })}
                  <td style={{ padding:'8px 14px', textAlign:'center', fontWeight:700, fontSize:13 }}>
                    {fmtHPlain(people.reduce((s,p) => s + (isCurrentWeek ? days.filter(d=>d<=yesterday) : days).reduce((ss,d) => ss+(byPerson[p][d]||0), 0), 0))}
                  </td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    );
  };

  // ── MONTH VIEW ─────────────────────────────────────────────────
  const renderMonth = () => {
    const { year, month } = monthYear;
    const weeks = getMonthWeeks(year, month);
    const canNext = new Date(year, month + 1, 1) <= new Date();
    const monthFrom = localDateStr(new Date(year, month, 1));
    const monthTo = localDateStr(new Date(year, month + 1, 0));
    const workingDays = countWorkingDays(monthFrom, monthTo);
    const monthTarget = workingDays * DAY_TARGET;

    return (
      <div>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
          <button onClick={() => setMonthYear(m => m.month===0?{year:m.year-1,month:11}:{year:m.year,month:m.month-1})}
            className="btn btn-secondary" style={{padding:'6px 12px'}}>‹</button>
          <span style={{ fontWeight:700, fontSize:15, minWidth:160, textAlign:'center' }}>{monthLabel(localDateStr(new Date(year,month,1)))}</span>
          <button onClick={() => canNext && setMonthYear(m => m.month===11?{year:m.year+1,month:0}:{year:m.year,month:m.month+1})}
            className="btn btn-secondary" style={{padding:'6px 12px', opacity:canNext?1:.4}}>›</button>
        </div>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', minWidth:400 }}>
            <thead>
              <tr style={{ borderBottom:'2px solid var(--border)' }}>
                <TH style={{ textAlign:'left', padding:'10px 14px', width:170 }}>Member</TH>
                {weeks.map(w => <TH key={w} style={{ minWidth:90 }}>{weekLabel(w)}</TH>)}
                <TH><div>Total</div><div style={{ fontWeight:400, fontSize:10, color:'var(--text-3)', marginTop:2 }}>{workingDays}d × 7.8h</div></TH>
              </tr>
            </thead>
            <tbody>
              {people.length === 0 && (
                <tr><td colSpan={weeks.length+2} style={{ padding:32, textAlign:'center', color:'var(--text-2)', fontSize:13 }}>No logged time for this month</td></tr>
              )}
              {people.map(person => {
                const map = byPerson[person] || {};
                const absMap = absByPerson[person] || {};
                const start = memberStart(person);
                const monthTotal = Object.entries(map).reduce((s,[d,v]) => d >= start ? s+v : s, 0);
                const personMonthTarget = (() => {
                  let t = 0;
                  let d = monthFrom > start ? monthFrom : start;
                  while (d <= monthTo) {
                    const dow = new Date(d+'T00:00:00').getDay();
                    if (dow >= 1 && dow <= 5 && !BE_HOLIDAYS.has(d)) {
                      t += Math.max(0, DAY_TARGET - (absMap[d] || 0));
                    }
                    d = addDays(d, 1);
                  }
                  return t;
                })();
                return (
                  <tr key={person} style={{ borderBottom:'1px solid var(--border)' }}>
                    <td style={{ padding:'10px 14px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ width:8, height:8, borderRadius:'50%', background:personColor(person), flexShrink:0 }} />
                        <span style={{ fontWeight:600, fontSize:13 }}>{shortName(person)}</span>
                      </div>
                    </td>
                    {weeks.map(w => {
                      const weekFri = addDays(w, 4);
                      if (weekFri < start) return <td key={w} style={{ padding:'6px 4px', textAlign:'center', color:'var(--text-3)', fontSize:13 }}>—</td>;
                      const weekSec = getWeekDays(w).filter(d => d >= start).reduce((s,d) => s+(map[d]||0), 0);
                      const weekTarget = getWeekDays(w)
                        .filter(d => d >= start && d >= monthFrom && d <= monthTo && !BE_HOLIDAYS.has(d))
                        .reduce((s,d) => s + Math.max(0, DAY_TARGET - (absMap[d]||0)), 0);
                      const cs = weekTarget > 0 ? cellStyle(weekSec, weekTarget) : { background:'transparent', color:'var(--text-3)', fontWeight:400 };
                      const showTarget = weekTarget > 0 && weekTarget < TARGET_SECONDS;
                      return (
                        <td key={w} style={{ padding:'6px 4px', textAlign:'center' }}>
                          <div style={{ padding:'6px 8px', borderRadius:8, fontSize:13, background:cs.background, color:cs.color, fontWeight:cs.fontWeight, display:'flex', flexDirection:'column', alignItems:'center', gap:1 }}>
                            <span>{weekSec ? fmtHPlain(weekSec) : '—'}</span>
                            {showTarget && <span style={{ fontSize:9, fontWeight:400, opacity:0.7 }}>/ {fmtHPlain(weekTarget)}</span>}
                          </div>
                        </td>
                      );
                    })}
                    <td style={{ padding:'8px 14px', textAlign:'center' }}>{totalBadge(monthTotal, personMonthTarget)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ── CUSTOM VIEW ────────────────────────────────────────────────
  const renderCustom = () => {
    const start = getMondayOf(customFrom);
    const weeks = [];
    let cur = start;
    while (cur <= customTo && weeks.length < 53) { weeks.push(cur); cur = addDays(cur, 7); }
    const customWorkingDays = countWorkingDays(customFrom, customTo);
    const customTarget = customWorkingDays * DAY_TARGET;

    return (
      <div>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:20, flexWrap:'wrap' }}>
          <input type="date" value={customFrom} min={MIN_DATE} max={customTo}
            onChange={e => setCustomFrom(e.target.value)} className="input" style={{width:140}} />
          <span style={{color:'var(--text-2)'}}>→</span>
          <input type="date" value={customTo} min={customFrom} max={today}
            onChange={e => setCustomTo(e.target.value)} className="input" style={{width:140}} />
          <button onClick={load} className="btn btn-primary" style={{padding:'6px 14px'}}>Apply</button>
        </div>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', minWidth:400 }}>
            <thead>
              <tr style={{ borderBottom:'2px solid var(--border)' }}>
                <TH style={{ textAlign:'left', padding:'10px 14px', width:170 }}>Member</TH>
                {weeks.map(w => <TH key={w} style={{ minWidth:90 }}>{weekLabel(w)}</TH>)}
                <TH><div>Total</div><div style={{ fontWeight:400, fontSize:10, color:'var(--text-3)', marginTop:2 }}>{customWorkingDays}d × 7.8h</div></TH>
              </tr>
            </thead>
            <tbody>
              {people.length === 0 && (
                <tr><td colSpan={weeks.length+2} style={{ padding:32, textAlign:'center', color:'var(--text-2)', fontSize:13 }}>No logged time for this period</td></tr>
              )}
              {people.map(person => {
                const map = byPerson[person] || {};
                const absMap = absByPerson[person] || {};
                const start = memberStart(person);
                const totalSec = Object.entries(map).reduce((s,[d,v]) => d >= start ? s+v : s, 0);
                const personCustomTarget = (() => {
                  let t = 0;
                  let d = customFrom > start ? customFrom : start;
                  while (d <= customTo) {
                    const dow = new Date(d+'T00:00:00').getDay();
                    if (dow >= 1 && dow <= 5 && !BE_HOLIDAYS.has(d)) {
                      t += Math.max(0, DAY_TARGET - (absMap[d] || 0));
                    }
                    d = addDays(d, 1);
                  }
                  return t;
                })();
                return (
                  <tr key={person} style={{ borderBottom:'1px solid var(--border)' }}>
                    <td style={{ padding:'10px 14px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ width:8, height:8, borderRadius:'50%', background:personColor(person), flexShrink:0 }} />
                        <span style={{ fontWeight:600, fontSize:13 }}>{shortName(person)}</span>
                      </div>
                    </td>
                    {weeks.map(w => {
                      const weekFri = addDays(w, 4);
                      if (weekFri < start) return <td key={w} style={{ padding:'6px 4px', textAlign:'center', color:'var(--text-3)', fontSize:13 }}>—</td>;
                      const weekSec = getWeekDays(w).filter(d => d >= start).reduce((s,d) => s+(map[d]||0), 0);
                      const weekTarget = getWeekDays(w)
                        .filter(d => d >= start && d >= customFrom && d <= customTo && !BE_HOLIDAYS.has(d))
                        .reduce((s,d) => s + Math.max(0, DAY_TARGET - (absMap[d]||0)), 0);
                      const cs = weekTarget > 0 ? cellStyle(weekSec, weekTarget) : { background:'transparent', color:'var(--text-3)', fontWeight:400 };
                      const showTarget = weekTarget > 0 && weekTarget < TARGET_SECONDS;
                      return (
                        <td key={w} style={{ padding:'6px 4px', textAlign:'center' }}>
                          <div style={{ padding:'6px 8px', borderRadius:8, fontSize:13, background:cs.background, color:cs.color, fontWeight:cs.fontWeight, display:'flex', flexDirection:'column', alignItems:'center', gap:1 }}>
                            <span>{weekSec ? fmtHPlain(weekSec) : '—'}</span>
                            {showTarget && <span style={{ fontSize:9, fontWeight:400, opacity:0.7 }}>/ {fmtHPlain(weekTarget)}</span>}
                          </div>
                        </td>
                      );
                    })}
                    <td style={{ padding:'8px 14px', textAlign:'center' }}>{totalBadge(totalSec, personCustomTarget)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ── COMPARE VIEW ───────────────────────────────────────────────
  const renderCompare = () => {
    const days = getWeekDays(compareWeekStart);
    const canPrev = compareWeekStart > MIN_DATE;
    const canNext = addDays(compareWeekStart, 7) <= today;
    const selected = compareMember || (people.length > 0 ? people[0] : '');

    const ANON_LABELS = ['Alpha','Beta','Gamma','Delta','Epsilon'];
    let anonIdx = 0;
    const anonMap = {};
    for (const p of people) {
      if (p !== selected) { anonMap[p] = ANON_LABELS[anonIdx++ % ANON_LABELS.length]; }
    }

    const isCurrentWeek = compareWeekStart === getMondayOf(today);
    const yesterday = addDays(today, -1);
    const effectiveDays2 = isCurrentWeek ? days.filter(d => d <= yesterday) : days;

    return (
      <div>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, flexWrap:'wrap' }}>
          <button onClick={() => canPrev && setCompareWeekStart(w => addDays(w,-7))}
            className="btn btn-secondary" style={{padding:'6px 12px', opacity:canPrev?1:.4}}>‹</button>
          <span style={{ fontWeight:700, fontSize:15, minWidth:180, textAlign:'center' }}>{weekLabel(compareWeekStart)}</span>
          <button onClick={() => canNext && setCompareWeekStart(w => addDays(w,7))}
            className="btn btn-secondary" style={{padding:'6px 12px', opacity:canNext?1:.4}}>›</button>
          <select value={selected} onChange={e => setCompareMember(e.target.value)}
            className="input" style={{ fontSize:13, minWidth:160 }}>
            {people.map(p => <option key={p} value={p}>{p.split(' ').map(w=>w[0]+w.slice(1).toLowerCase()).join(' ')}</option>)}
          </select>
        </div>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', minWidth:580 }}>
            <thead>
              <tr style={{ borderBottom:'2px solid var(--border)' }}>
                <TH style={{ textAlign:'left', padding:'10px 14px', width:170 }}>Member</TH>
                {days.map(d => {
                  const { day, num } = dayLabel(d);
                  return <TH key={d}><div>{day}</div><div style={{ fontSize:14, color:'var(--text)', marginTop:2, fontWeight:700 }}>{num}</div></TH>;
                })}
                <TH>Total</TH>
              </tr>
            </thead>
            <tbody>
              {people.length === 0 && (
                <tr><td colSpan={8} style={{ padding:32, textAlign:'center', color:'var(--text-2)', fontSize:13 }}>No data</td></tr>
              )}
              {[selected, ...people.filter(p => p !== selected)].map((person, idx) => {
                const isSelected = person === selected;
                const map = byPerson[person] || {};
                const absMap = absByPerson[person] || {};
                const weekTotal = effectiveDays2.reduce((s, d) => s + (map[d] || 0), 0);
                const personTarget = effectiveDays2.reduce((s, d) => BE_HOLIDAYS.has(d) ? s : s + Math.max(0, DAY_TARGET - (absMap[d]||0)), 0);
                const label = isSelected
                  ? person.split(' ').map((w,i) => i===0 ? w[0].toUpperCase()+w.slice(1).toLowerCase() : w.toUpperCase()).join(' ')
                  : anonMap[person];
                const dot = isSelected ? personColor(person) : '#94a3b8';
                return (
                  <tr key={person} style={{ borderBottom:'1px solid var(--border)', opacity: isSelected ? 1 : 0.65 }}>
                    <td style={{ padding:'10px 14px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ width:8, height:8, borderRadius:'50%', background:dot, flexShrink:0 }} />
                        <span style={{ fontWeight: isSelected ? 700 : 400, fontSize:13, color: isSelected ? 'var(--text)' : 'var(--text-2)' }}>{label}</span>
                      </div>
                    </td>
                    {days.map(d => {
                      const absSec = absMap[d] || 0;
                      const sec = map[d] || 0;
                      const cs = cellStyle(sec, DAY_TARGET - absSec > 0 ? DAY_TARGET - absSec : DAY_TARGET);
                      return (
                        <td key={d} style={{ padding:'6px 4px', textAlign:'center' }}>
                          <div style={{ padding:'6px 4px', borderRadius:8, fontSize:13,
                            background: isSelected ? cs.background : 'transparent',
                            color: isSelected ? cs.color : 'var(--text-2)',
                            fontWeight: isSelected ? cs.fontWeight : 400 }}>
                            <div style={{ display:'flex', flexDirection:'column', alignItems:'center' }}>
                              <span>{sec ? fmtHPlain(sec) : '—'}</span>
                              {absLabel(absSec)}
                              {holidayBadge(d)}
                            </div>
                          </div>
                        </td>
                      );
                    })}
                    <td style={{ padding:'8px 14px', textAlign:'center' }}>{totalBadge(weekTotal, personTarget)}</td>
                  </tr>
                );
              })}
            </tbody>
            {people.length > 0 && (
              <tfoot>
                <tr style={{ borderTop:'2px solid var(--border)', background:'var(--bg-2)' }}>
                  <td style={{ padding:'10px 14px', fontWeight:700, fontSize:12, color:'var(--text-2)' }}>Team avg</td>
                  {days.map(d => {
                    const tot = people.reduce((s, p) => s + ((byPerson[p]||{})[d]||0), 0);
                    const avg = people.length > 0 ? Math.round(tot / people.length) : 0;
                    const isFuture = isCurrentWeek && d >= today;
                    return <td key={d} style={{ padding:'8px 4px', textAlign:'center', fontSize:13, fontWeight:700,
                      color: isFuture ? 'var(--text-3)' : 'var(--text)', opacity: isFuture ? 0.4 : 1 }}>
                      {avg ? fmtHPlain(avg) : '—'}
                    </td>;
                  })}
                  <td style={{ padding:'8px 14px', textAlign:'center', fontSize:13, fontWeight:700 }}>
                    {fmtHPlain(Math.round(people.reduce((s,p) => s + effectiveDays2.reduce((ss,d) => ss+((byPerson[p]||{})[d]||0),0), 0) / (people.length||1)))}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div style={{ marginTop:12, fontSize:11, color:'var(--text-3)' }}>
          Other team members shown anonymously. Their performance data is visible but not attributed.
        </div>
      </div>
    );
  };

  return (
    <div>
      <ReportNote what="Billable versus non-billable logged time per member." from={fromDate} to={toDate} />
      <div className="no-print" style={{ display:'flex', alignItems:'center', gap:16, marginBottom:20, flexWrap:'wrap' }}>
        <div style={{ display:'flex', gap:4, background:'var(--bg-2)', borderRadius:10, padding:4 }}>
          {[['week','Week'],['month','Month'],['custom','Custom'],['compare','Compare']].map(([v,l]) => (
            <button key={v} onClick={() => setView(v)}
              style={{ padding:'6px 16px', borderRadius:8, border:'none', cursor:'pointer', fontWeight:600, fontSize:13,
                background: view===v ? 'var(--card)' : 'transparent',
                color: view===v ? 'var(--text)' : 'var(--text-2)',
                boxShadow: view===v ? '0 1px 3px rgba(0,0,0,.12)' : 'none' }}>
              {l}
            </button>
          ))}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'var(--text-3)' }}>
          <span style={{ width:10, height:10, borderRadius:3, background:'#16a34a22', border:'1px solid #16a34a', display:'inline-block' }} /> ≥39h
          <span style={{ width:10, height:10, borderRadius:3, background:'#d9770622', border:'1px solid #d97706', display:'inline-block', marginLeft:8 }} /> 30–39h
          <span style={{ width:10, height:10, borderRadius:3, background:'#dc262622', border:'1px solid #dc2626', display:'inline-block', marginLeft:8 }} /> &lt;30h
        </div>
        <button onClick={() => window.print()} className="btn btn-secondary" style={{ marginLeft:'auto', padding:'6px 14px', fontSize:13 }}>
          Export PDF
        </button>
      </div>

      <div className="card">
        <div style={{ padding:'16px 20px 24px' }}>
          {loading ? (
            <div style={{padding:32, textAlign:'center', color:'var(--text-2)'}}>Loading worklog data…</div>
          ) : view === 'week' ? renderWeek()
            : view === 'month' ? renderMonth()
            : view === 'compare' ? renderCompare()
            : renderCustom()}
        </div>
      </div>
      <style>{`
        @media print {
          .no-print, .sidebar, .topbar, .topnav, nav { display: none !important; }
          .main { margin: 0 !important; padding: 8px !important; }
          body { background: white !important; }
          .card { box-shadow: none !important; border: 1px solid #e5e7eb !important; }
        }
      `}</style>
    </div>
  );
}
